import { postMissionImportFishDataMutation } from '@/client/@tanstack/react-query.gen'
import type { PostMissionImportFishDataData } from '@/client/types.gen'
import { showToast, showToastSuccess } from '@/lib'
import { useMutation } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import styles from './Style.module.scss'

type FishRecord = NonNullable<PostMissionImportFishDataData['body']>[number]

const MAX_ROWS_PER_IMPORT = 1000
const IMPORT_BATCH_DELAY_MS = 500

const wait = (milliseconds: number) =>
  new Promise<void>((resolve) => window.setTimeout(resolve, milliseconds))

const normalizeHeader = (header: string) =>
  header
    .trim()
    .replace(/^\uFEFF/, '')
    .toLowerCase()
    .replace(/[\s-]/g, '_')

// Handles quoted values and commas inside quoted values without adding a CSV dependency.
const parseCsvLine = (line: string) => {
  const values: string[] = []
  let value = ''
  let quoted = false

  for (let index = 0; index < line.length; index += 1) {
    const character = line[index]
    const nextCharacter = line[index + 1]

    if (character === '"' && quoted && nextCharacter === '"') {
      value += '"'
      index += 1
    } else if (character === '"') {
      quoted = !quoted
    } else if (character === ',' && !quoted) {
      values.push(value.trim())
      value = ''
    } else {
      value += character
    }
  }

  values.push(value.trim())
  return values
}

const parseFishCsv = (csv: string): FishRecord[] => {
  const lines = csv
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .filter((line) => line.trim())
  if (lines.length < 2) throw new Error('File CSV không có dữ liệu')

  const headers = parseCsvLine(lines[0]).map(normalizeHeader)
  const requiredHeaders = [
    'vopenid',
    'total_fish_cnt',
    'heaviest_weight',
    'heaviest_itemid',
  ]
  const indexes = requiredHeaders.map((header) => headers.indexOf(header))

  if (indexes.some((index) => index < 0)) {
    throw new Error(
      'CSV phải có các cột: vopenid, total_fish_cnt, heaviest_weight, heaviest_itemid',
    )
  }

  const dataLines = lines.slice(1)

  return dataLines.map((line, rowIndex) => {
    const columns = parseCsvLine(line)
    const [
      vopenidIndex,
      totalFishCntIndex,
      heaviestWeightIndex,
      heaviestItemidIndex,
    ] = indexes
    const vopenid = columns[vopenidIndex]
    const totalFishCnt = Number(columns[totalFishCntIndex])
    const heaviestWeight = columns[heaviestWeightIndex]
    const heaviestItemid = columns[heaviestItemidIndex]

    if (
      !vopenid ||
      !heaviestWeight ||
      !heaviestItemid ||
      !Number.isFinite(totalFishCnt)
    ) {
      throw new Error(`Dòng ${rowIndex + 2} không hợp lệ`)
    }

    return { vopenid, totalFishCnt, heaviestWeight, heaviestItemid }
  })
}

const AdminFishImporter = () => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [fileName, setFileName] = useState('')
  const importMutation = useMutation(postMissionImportFishDataMutation())

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    setFileName(file.name)

    try {
      const records = parseFishCsv(await file.text())
      let importedRows = 0

      for (
        let start = 0;
        start < records.length;
        start += MAX_ROWS_PER_IMPORT
      ) {
        const batch = records.slice(start, start + MAX_ROWS_PER_IMPORT)
        const result = await importMutation.mutateAsync({ body: batch })
        importedRows += result.importedRows

        if (start + MAX_ROWS_PER_IMPORT < records.length) {
          await wait(IMPORT_BATCH_DELAY_MS)
        }
      }

      showToastSuccess(
        `Import thành công ${importedRows.toLocaleString('vi-VN')} dòng`,
      )
      setFileName('')
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Không thể import dữ liệu'
      showToast(message)
    }
  }

  return (
    <div className={styles.wrapper}>
      <input
        ref={inputRef}
        className={styles.input}
        type="file"
        accept=".csv,text/csv"
        onChange={handleFileChange}
      />
      <button
        className={styles.button}
        type="button"
        disabled={importMutation.isPending}
        onClick={() => inputRef.current?.click()}
        title={fileName || 'Import FishRecord CSV'}
      >
        {importMutation.isPending ? 'Đang import…' : 'Import fish'}
      </button>
    </div>
  )
}

export default AdminFishImporter
